/**
 * React Query hooks — Candidates, intake/dedup, resume upload.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	RecruitmentApiService,
	type IntakeBody,
	type LinkApplicationBody,
} from '../../../services/recruitmentApi';

const KEY = ['recruitment', 'candidates'] as const;

export const useCandidate = (id: number | undefined) =>
	useQuery({
		queryKey: [...KEY, 'detail', id],
		enabled: typeof id === 'number' && !Number.isNaN(id),
		queryFn: async () => {
			const resp = await RecruitmentApiService.getCandidate(id as number);
			return resp.data; // { candidate, applications }
		},
	});

/**
 * The intake mutation. Returns the raw IntakeResult so the caller can branch on
 * `data.outcome` (created / linked / ambiguous) — the whole point of the flow.
 */
export const useIntake = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body: IntakeBody) => {
			const resp = await RecruitmentApiService.intake(body);
			return resp.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
			qc.invalidateQueries({ queryKey: ['recruitment', 'jobs'] });
		},
	});
};

/** Add an application to an existing candidate (ambiguous pick / add-to-existing). */
export const useLinkApplication = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ candidateId, body }: { candidateId: number; body: LinkApplicationBody }) => {
			const resp = await RecruitmentApiService.linkApplication(candidateId, body);
			return resp.data;
		},
		onSuccess: (_data, variables) => {
			qc.invalidateQueries({ queryKey: ['recruitment', 'applications'] });
			qc.invalidateQueries({ queryKey: [...KEY, 'detail', variables.candidateId] });
		},
	});
};

export const useUploadResume = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			candidateId,
			file,
			extractedText,
		}: {
			candidateId: number;
			file: File;
			extractedText?: string;
		}) => {
			const resp = await RecruitmentApiService.uploadResume(candidateId, file, extractedText);
			return resp.data; // { resume_id, text_indexed }
		},
		onSuccess: (_data, variables) => {
			qc.invalidateQueries({ queryKey: [...KEY, 'detail', variables.candidateId] });
		},
	});
};
